import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { AlertCircleIcon, InboxIcon, ShieldCheckIcon } from 'lucide-react';
import { type AuditActor, type AuditLogEntry, isVoorbeeld } from '@kennisbank/shared';
import { Layout } from '../components/Layout.js';
import { ApiError, apiGet } from '../api/client.js';

/**
 * Beheer -> Audit log — read-only inzage op de append-only `audit_log`
 * collectie (NEN 7510 aantoonbaarheid). RBAC: suppohrt_admin, gated aan de
 * route (RoleGuard) en server-side. Nieuwste eerst, filter op gebruiker /
 * actie(-prefix) / periode, "meer laden" via cursor.
 */

interface AuditPage {
  entries: AuditLogEntry[];
  actors?: Record<string, AuditActor>;
  nextCursor: string | null;
}

const PAGE_SIZE = 50;

function formatMoment(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('nl-NL');
}

export function BeheerAuditLogPage(): JSX.Element {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [actors, setActors] = useState<Record<string, AuditActor>>({});
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Filtervelden — apart van de toegepaste query zodat typen niet elke
  // toetsaanslag een request afvuurt; "Toepassen" reset de lijst + cursor.
  const [userId, setUserId] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [applied, setApplied] = useState({ userId: '', action: '', from: '', to: '' });

  const fetchPage = useCallback(
    async (afterCursor: string | null): Promise<AuditPage> =>
      apiGet<AuditPage>('/api/v1/beheer/audit-log', {
        limit: PAGE_SIZE,
        ...(applied.userId ? { userId: applied.userId } : {}),
        ...(applied.action ? { action: applied.action } : {}),
        ...(applied.from ? { from: applied.from } : {}),
        ...(applied.to ? { to: applied.to } : {}),
        ...(afterCursor ? { cursor: afterCursor } : {}),
      }),
    [applied],
  );

  const loadFirst = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const page = await fetchPage(null);
      setEntries(page.entries);
      setActors(page.actors ?? {});
      setCursor(page.nextCursor);
    } catch (err) {
      setErrorMsg(
        err instanceof ApiError
          ? `Laden mislukt (${err.status}): ${err.message}`
          : 'Audit log kon niet geladen worden.',
      );
    } finally {
      setLoading(false);
    }
  }, [fetchPage]);

  useEffect(() => {
    void loadFirst();
  }, [loadFirst]);

  async function loadMore(): Promise<void> {
    if (!cursor) return;
    setLoadingMore(true);
    setErrorMsg(null);
    try {
      const page = await fetchPage(cursor);
      setEntries((prev) => [...prev, ...page.entries]);
      setActors((prev) => ({ ...prev, ...page.actors }));
      setCursor(page.nextCursor);
    } catch (err) {
      setErrorMsg(
        err instanceof ApiError ? `Laden mislukt (${err.status})` : 'Meer laden mislukt.',
      );
    } finally {
      setLoadingMore(false);
    }
  }

  function applyFilters(e: FormEvent): void {
    e.preventDefault();
    setApplied({ userId: userId.trim(), action: action.trim(), from, to });
  }

  const inputClass =
    'rounded-md border border-ring2 bg-white px-3 py-1.5 text-sm text-ink-900 placeholder:text-ink-900/40';

  return (
    <Layout>
      <div className="mx-auto w-full max-w-5xl px-4 py-6">
        <header className="mb-4">
          <h1 className="flex items-center gap-2 text-xl font-bold text-ink-900">
            <ShieldCheckIcon size={20} className="text-brand-700" /> Audit log
          </h1>
          <p className="mt-1 text-sm text-ink-900/65">
            Onveranderbaar overzicht van beveiligingsgevoelige acties (logins, beheer-mutaties, rol-
            en gebruikerswijzigingen) voor aantoonbaarheid. Nieuwste bovenaan.
          </p>
        </header>

        <form
          onSubmit={applyFilters}
          className="mb-4 flex flex-wrap items-end gap-3 rounded-2xl border border-ring2 bg-white p-4"
        >
          <label className="flex flex-col gap-1 text-[12px] font-medium text-ink-900/70">
            Gebruiker (id)
            <input
              type="text"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="bijv. dev-user-admin"
              className={inputClass}
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-medium text-ink-900/70">
            Actie (prefix)
            <input
              type="text"
              value={action}
              onChange={(e) => setAction(e.target.value)}
              placeholder="bijv. login. of user."
              className={inputClass}
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-medium text-ink-900/70">
            Van
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className={inputClass}
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-medium text-ink-900/70">
            Tot
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className={inputClass}
            />
          </label>
          <button
            type="submit"
            className="rounded-lg bg-brand-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Toepassen
          </button>
        </form>

        {errorMsg && (
          <div className="mb-4 inline-flex items-center gap-1.5 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertCircleIcon size={14} /> {errorMsg}
          </div>
        )}

        {loading ? (
          <div className="rounded-2xl border border-ring2 bg-white px-6 py-10 text-center text-sm text-ink-900/55">
            Laden…
          </div>
        ) : entries.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-2xl border border-ring2 bg-white px-6 py-12 text-center text-sm text-ink-900/55">
            <InboxIcon size={28} className="text-ink-900/30" />
            Geen audit-regels in deze weergave.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-ring2 bg-white shadow-card">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-ring2 text-[12px] uppercase tracking-wide text-ink-900/55">
                  <th className="px-4 py-2.5 font-semibold">Tijdstip</th>
                  <th className="px-4 py-2.5 font-semibold">Gebruiker</th>
                  <th className="px-4 py-2.5 font-semibold">Actie</th>
                  <th className="px-4 py-2.5 font-semibold">Object</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} className="border-b border-ring2/60 last:border-0 align-top">
                    <td className="whitespace-nowrap px-4 py-2.5 text-ink-900/70 tabular-nums">
                      {formatMoment(e.at)}
                    </td>
                    <td className="px-4 py-2.5 text-ink-900/80" title={e.actorUserId}>
                      {e.actorUserId ? (
                        (actors[e.actorUserId]?.naam ?? e.actorUserId)
                      ) : (
                        <span className="text-ink-900/40">systeem</span>
                      )}
                      {e.actorUserId && actors[e.actorUserId]?.email && (
                        <span className="block text-[11px] text-ink-900/45">
                          {actors[e.actorUserId]!.email}
                        </span>
                      )}
                      {e.actorRole && (
                        <span className="ml-1.5 text-[11px] text-ink-900/45">({e.actorRole})</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className="rounded-sm bg-brand-50 px-2 py-0.5 font-medium text-brand-700"
                        title={e.action}
                      >
                        {e.action === 'document.downloaded'
                          ? isVoorbeeld(e)
                            ? 'Bekeken'
                            : 'Gedownload'
                          : e.action}
                      </span>
                    </td>
                    <td
                      className="max-w-xs truncate px-4 py-2.5 text-ink-900/60"
                      title={e.targetPath}
                    >
                      {(typeof e.context?.documentTitle === 'string' && e.context.documentTitle) ||
                        e.targetPath ||
                        '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {cursor && !loading && (
          <div className="mt-4 flex justify-center">
            <button
              type="button"
              onClick={() => void loadMore()}
              disabled={loadingMore}
              className="rounded-lg border border-ring2 bg-white px-4 py-2 text-sm font-medium text-ink-900 hover:bg-brand-50 disabled:opacity-50"
            >
              {loadingMore ? 'Laden…' : 'Meer laden'}
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}
